import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';

/** Server-side cap on the stored reason (db_ops trims to the same length). */
const REASON_MAX = 2000;

interface DeleteAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** `reason` is undefined when the user left the optional box empty.
     *  Resolves with the scheduled deletion date the server returned (ISO
     *  string) so the success screen can name the day, or nothing. */
    onConfirm: (reason?: string) => Promise<string | void>;
}

/** "October 3, 2026" from the server's ISO stamp; the raw string if it does
 *  not parse, so the user is never shown "Invalid Date". */
const fmtDeletionDate = (iso: string): string => {
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose, onConfirm }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [reason, setReason] = useState('');
    const [scheduledFor, setScheduledFor] = useState<string | null>(null);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setIsSubmitting(false);
            setShowSuccess(false);
            setReason('');
            setScheduledFor(null);
        }
    }, [isOpen]);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        try {
            const trimmed = reason.trim();
            const when = await onConfirm(trimmed ? trimmed : undefined);
            // If we reach here, the request was successful
            setScheduledFor(typeof when === 'string' && when ? when : null);
            setShowSuccess(true);
        } catch (error) {
            console.error('Error in delete account confirmation:', error);
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setShowSuccess(false);
        setIsSubmitting(false);
        onClose();
    };

    if (showSuccess) {
        return (
            <Modal isOpen={isOpen} onClose={handleClose} title="Account Deletion Scheduled">
                <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <span className="text-2xl">⚠️</span>
                        <div className="flex-1">
                            <h3 className="text-grayscale-1300 font-grotesk text-base font-medium mb-2">
                                Your account is scheduled for deletion
                            </h3>
                            <p className="text-grayscale-1300 font-grotesk text-sm leading-5 mb-2">
                                {scheduledFor
                                    ? `Your account will be deleted on ${fmtDeletionDate(scheduledFor)}. `
                                    : 'Your account will be deleted on the date in your confirmation email. '}
                                You will receive a confirmation email shortly.
                            </p>
                            <p className="text-grayscale-1300 font-grotesk text-sm leading-5 font-medium">
                                To stop the deletion, please reply to the email we sent you.
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={handleClose} 
                        className="flex py-2.5 px-4 justify-center items-center gap-2 rounded-full bg-primary-og-600 shadow-custom-light"
                    >
                        <span className="text-white text-center font-grotesk text-sm font-medium">Close</span>
                    </button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Delete Account">
            <div className="flex flex-col gap-4">
                <h2 className="text-grayscale-1300 font-grotesk text-base font-medium">
                    Are you sure you want to delete your account?
                </h2>
                <p className="text-grayscale-1300 font-grotesk text-sm leading-5">
                    After deletion, exclusive access to the platform will no longer be available.
                </p>
                <label className="flex flex-col gap-1.5">
                    <span className="text-grayscale-1300 font-grotesk text-sm font-medium">
                        Why are you leaving? (optional)
                    </span>
                    <textarea
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        disabled={isSubmitting}
                        rows={3}
                        maxLength={REASON_MAX}
                        placeholder="Anything you tell us helps us improve."
                        className="w-full px-3 py-2 rounded-lg border border-grayscale-400 bg-white text-grayscale-1300 font-grotesk text-sm leading-5 resize-none focus:outline-none focus:border-primary-og-600 disabled:opacity-50"
                    />
                </label>
                <div className="flex items-center gap-3 self-stretch mt-4">
                    <button 
                        onClick={handleConfirm} 
                        disabled={isSubmitting}
                        className="flex py-2.5 px-4 justify-center items-center gap-2 flex-1 rounded-full border border-primary-og-600 bg-white shadow-custom-light disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span className="text-grayscale-1300 text-center font-grotesk text-sm font-medium">
                            {isSubmitting ? 'Processing...' : 'Yes, continue'}
                        </span>
                    </button>
                    <button 
                        onClick={handleClose} 
                        disabled={isSubmitting}
                        className="flex py-2.5 px-4 justify-center items-center gap-2 flex-1 rounded-full bg-primary-og-600 shadow-custom-light disabled:opacity-50"
                    >
                        <span className="text-white text-center font-grotesk text-sm font-medium">No, cancel</span>
                    </button>
                </div>
            </div>
        </Modal>
    );
};
